import { useCallback, useRef } from 'react';

/** Minimum horizontal pixel distance before a pointer-up commits as a swipe. */
const SWIPE_THRESHOLD_PX = 50;
/** Below this absolute horizontal delta the pointer is treated as a tap. */
const HORIZONTAL_INTENT_PX = 10;
/** Vertical movement ratio that classifies the gesture as "scroll / drag"
 *  rather than horizontal swipe. If |dy| > |dx| × this, we bail. */
const VERTICAL_DOMINANCE_RATIO = 1.2;

type Direction = 'prev' | 'next';

type Options = {
  /** Called when the user finishes a swipe past the threshold. */
  onSwipe: (direction: Direction) => void;
  /** Optional override — if `true`, swipes are ignored (e.g. during the
   *  in-flight slide transition). */
  disabled?: boolean;
  /** Document text direction. In RTL (Arabic) the natural "next" gesture
   *  is left-to-right (finger moves rightward) — mirror of LTR. Defaults
   *  to LTR so callers in pure-Latin contexts don't need to opt in. */
  rtl?: boolean;
};

type Handlers = {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
};

/**
 * Lightweight horizontal-swipe handler for the intro overlay.
 *
 * Behavior:
 * - Only PRIMARY pointers (left mouse / first touch) start tracking.
 * - On move: if vertical movement dominates (`|dy| > |dx| × 1.2`), we
 *   release pointer capture so the canvas below can orbit / drag.
 * - On up: if final horizontal delta exceeds `SWIPE_THRESHOLD_PX`, fire
 *   `onSwipe('prev' | 'next')`. Direction interpretation depends on the
 *   `rtl` option — LTR maps leftward swipe (negative `dx`) to `next`
 *   (reading-forward in Latin); RTL maps rightward swipe to `next`
 *   (reading-forward in Arabic).
 *
 * Why custom (vs Swiper): the new design needs crossfade + slide-up
 * animations on slide change. Swiper's built-in transform-based slide
 * animation fights that. We just take the swipe input here and hand off
 * to React-state-driven animation in the parent.
 */
export function useSlideSwipe({ onSwipe, disabled, rtl }: Options): Handlers {
  const stateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    captured: boolean;
    bailedOut: boolean;
  } | null>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (disabled) return;
      if (!e.isPrimary) return;
      // Mouse: left button only.
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      stateRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        captured: false,
        bailedOut: false,
      };
    },
    [disabled],
  );

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const state = stateRef.current;
    if (!state || state.pointerId !== e.pointerId) return;
    if (state.bailedOut) return;

    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Hand off to canvas if motion is dominantly vertical (orbit drag,
    // page scroll, etc.). Once we bail out, this pointer is dead for us.
    if (absDy > HORIZONTAL_INTENT_PX && absDy > absDx * VERTICAL_DOMINANCE_RATIO) {
      state.bailedOut = true;
      if (state.captured) {
        try {
          (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
        } catch {
          // capture may have already been released
        }
      }
      return;
    }

    // Horizontal intent confirmed — capture so we get pointerup even if
    // the finger drifts outside the curtain.
    if (!state.captured && absDx > HORIZONTAL_INTENT_PX) {
      state.captured = true;
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        // capture failure is fine — we'll still see pointerup via
        // the document if the browser allows.
      }
    }
  }, []);

  const finish = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const state = stateRef.current;
      if (!state || state.pointerId !== e.pointerId) return;
      stateRef.current = null;
      if (state.bailedOut) return;
      const dx = e.clientX - state.startX;
      if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
      // LTR: leftward swipe (dx < 0) advances reading direction → `next`.
      // RTL: rightward swipe (dx > 0) advances reading direction → `next`.
      const physicalNext = dx < 0;
      const logicalNext = rtl ? !physicalNext : physicalNext;
      onSwipe(logicalNext ? 'next' : 'prev');
    },
    [onSwipe, rtl],
  );

  const onPointerUp = finish;
  const onPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const state = stateRef.current;
      if (state && state.pointerId === e.pointerId) {
        stateRef.current = null;
      }
    },
    [],
  );

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
